import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["dropzone", "item"]
  static values = { createUrl: String }

  connect() {
  }

  get createActivityUrl() {
    // Use the configured URL if provided, otherwise fall back to default
    return this.hasCreateUrlValue ? this.createUrlValue : '/inloops/render_activity_card'
  }

  // Called when drag starts
  dragStart(event) {
    const item = event.currentTarget
    event.dataTransfer.effectAllowed = "copy"
    event.dataTransfer.setData("text/plain", JSON.stringify({
      title: item.dataset.activityTitle,
      type: item.dataset.activityType,
      description: item.dataset.activityDescription,
      badge: item.dataset.activityBadge,
      icon: item.dataset.activityIcon
    }))

    // Add visual feedback
    item.classList.add("opacity-50")
  }

  // Called when drag ends
  dragEnd(event) {
    event.currentTarget.classList.remove("opacity-50")
  }

  // Called when dragging over drop zone
  dragOver(event) {
    // Check if this is a reorder operation
    const types = event.dataTransfer.types || []
    if (types.includes("application/x-activity-reorder")) {
      return // Let activity-reorder controller handle this
    }

    event.preventDefault()
    event.stopPropagation()
    event.dataTransfer.dropEffect = "copy"

    const dropzone = event.currentTarget

    // Check if we're over an activity card
    const activityCard = event.target.closest('[data-activity-card]')
    if (activityCard && dropzone.contains(activityCard)) {
      // Highlight the specific activity card being hovered
      const rect = activityCard.getBoundingClientRect()
      const midpoint = rect.top + rect.height / 2

      // Remove all existing drop indicators
      dropzone.querySelectorAll('[data-activity-card]').forEach(card => {
        card.classList.remove('border-t-2', 'border-b-2', 'border-t-[#5a36e6]', 'border-b-[#5a36e6]')
      })

      // Add drop indicator
      if (event.clientY < midpoint) {
        activityCard.classList.add('border-t-2', 'border-t-[#5a36e6]')
      } else {
        activityCard.classList.add('border-b-2', 'border-b-[#5a36e6]')
      }
    } else {
      // Remove all drop indicators when not over a card
      dropzone.querySelectorAll('[data-activity-card]').forEach(card => {
        card.classList.remove('border-t-2', 'border-b-2', 'border-t-[#5a36e6]', 'border-b-[#5a36e6]')
      })
    }

    // Add purple background to the dropzone
    dropzone.classList.add("border-[#5a36e6]", "bg-[#f5f3ff]")

    // Check if there are multiple phases
    const allPhases = document.querySelectorAll('[data-phase-id]')
    const hasMultiplePhases = allPhases.length > 1

    // If single phase, highlight the entire parent container
    if (!hasMultiplePhases) {
      const parentContainer = dropzone.closest('.bg-\\[\\#FAFAFB\\]')
      if (parentContainer) {
        parentContainer.classList.remove('bg-[#FAFAFB]')
        parentContainer.classList.add('bg-[#f5f3ff]')
      }
    }
  }

  // Called when leaving drop zone
  dragLeave(event) {
    // Only remove highlight if we're actually leaving the dropzone
    // (not just moving over child elements)
    const dropzone = event.currentTarget
    const rect = dropzone.getBoundingClientRect()
    const x = event.clientX
    const y = event.clientY

    // Check if the mouse is still within the dropzone bounds
    if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
      return // Still inside, don't remove highlight
    }

    // Remove all drop indicators from activity cards
    dropzone.querySelectorAll('[data-activity-card]').forEach(card => {
      card.classList.remove('border-t-2', 'border-b-2', 'border-t-[#5a36e6]', 'border-b-[#5a36e6]')
    })

    dropzone.classList.remove("border-[#5a36e6]", "bg-[#f5f3ff]")

    // Check if there are multiple phases
    const allPhases = document.querySelectorAll('[data-phase-id]')
    const hasMultiplePhases = allPhases.length > 1

    // If single phase, restore the parent container's original background
    if (!hasMultiplePhases) {
      const parentContainer = dropzone.closest('.bg-\\[\\#f5f3ff\\]')
      if (parentContainer) {
        parentContainer.classList.remove('bg-[#f5f3ff]')
        parentContainer.classList.add('bg-[#FAFAFB]')
      }
    }
  }

  // Called when item is dropped
  drop(event) {
    // Check if this is a reorder operation - if so, ignore it
    const reorderData = event.dataTransfer.getData("application/x-activity-reorder")
    if (reorderData) {
      return // Let activity-reorder controller handle this
    }

    event.preventDefault()
    event.stopPropagation()

    const dropzone = event.currentTarget

    // Remove all drop indicators from activity cards
    dropzone.querySelectorAll('[data-activity-card]').forEach(card => {
      card.classList.remove('border-t-2', 'border-b-2', 'border-t-[#5a36e6]', 'border-b-[#5a36e6]')
    })

    dropzone.classList.remove("border-[#5a36e6]", "bg-[#f5f3ff]")

    // Check if there are multiple phases
    const allPhases = document.querySelectorAll('[data-phase-id]')
    const hasMultiplePhases = allPhases.length > 1

    // If single phase, restore the parent container's original background
    if (!hasMultiplePhases) {
      const parentContainer = dropzone.closest('.bg-\\[\\#f5f3ff\\]')
      if (parentContainer) {
        parentContainer.classList.remove('bg-[#f5f3ff]')
        parentContainer.classList.add('bg-[#FAFAFB]')
      }
    }

    try {
      const data = JSON.parse(event.dataTransfer.getData("text/plain"))

      // Find if dropped on or near an activity card to determine insert position
      const dropTarget = event.target.closest('[data-activity-card]')

      this.addActivityCard(data, dropzone, dropTarget, event)
    } catch (error) {
      // Error parsing drop data
    }
  }

  // Add the activity card to the activity area
  async addActivityCard(activityData, dropzone, dropTarget = null, event = null) {
    // Find the empty state and activities container within the specific dropzone
    const emptyState = dropzone.querySelector('[data-activity-empty-state]')
    if (emptyState) {
      emptyState.classList.add('hidden')
    }

    // Show activities container within this specific dropzone
    let activitiesContainer = dropzone.querySelector('[data-activities-container]')
    if (!activitiesContainer) {
      // Create activities container if it doesn't exist (for dynamic dropzones)
      activitiesContainer = document.createElement('div')
      activitiesContainer.setAttribute('data-activities-container', '')
      activitiesContainer.setAttribute('data-controller', 'activity-reorder')
      activitiesContainer.className = 'flex flex-col gap-6 w-full'

      // Insert before empty state if it exists, otherwise append to dropzone
      if (emptyState) {
        dropzone.insertBefore(activitiesContainer, emptyState)
      } else {
        dropzone.appendChild(activitiesContainer)
      }
    }

    // Always ensure container is visible
    activitiesContainer.classList.remove('hidden')

    // Determine insert position
    let insertBeforeCard = null
    if (dropTarget && event) {
      // Get the bounding rect of the drop target
      const rect = dropTarget.getBoundingClientRect()
      const midpoint = rect.top + rect.height / 2

      // If dropped in the upper half, insert before; otherwise insert after
      if (event.clientY < midpoint) {
        insertBeforeCard = dropTarget
      } else {
        insertBeforeCard = dropTarget.nextElementSibling
      }
    }

    // Calculate activity number based on drop position (1-based index)
    const cards = Array.from(activitiesContainer.querySelectorAll('[data-activity-card]'))
    let activityNumber = cards.length + 1
    if (insertBeforeCard) {
      const insertIndex = cards.indexOf(insertBeforeCard)
      if (insertIndex !== -1) {
        activityNumber = insertIndex + 1
      }
    }

    // Get the phase ID from the dropzone's parent phase container
    const phaseContainer = dropzone.closest('[data-phase-id]')
    const phaseId = phaseContainer ? phaseContainer.dataset.phaseId : null

    // Fetch activity card HTML from server
    try {
      const response = await fetch(this.createActivityUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          activity: activityData,
          activity_number: activityNumber,
          activity_phase_id: phaseId
        })
      })

      if (response.ok) {
        const html = await response.text()

        // Create a temporary container to parse the HTML
        const temp = document.createElement('div')
        temp.innerHTML = html

        // Get the activity card element
        const newCard = temp.firstElementChild

        // Insert at the determined position
        if (newCard) {
          if (insertBeforeCard) {
            activitiesContainer.insertBefore(newCard, insertBeforeCard)
          } else {
            activitiesContainer.appendChild(newCard)
          }
        }

        // Update activity numbers after adding new activity
        this.updateActivityNumbers(activitiesContainer)

        // Update activity count badge
        this.updateActivityCountBadge(dropzone)

        // Dispatch custom event to notify activity selectors to refresh
        window.dispatchEvent(new CustomEvent('activity-list-changed', {
          detail: { type: 'add' }
        }))
      } else {
        const errorText = await response.text()
        alert('Error adding activity: ' + errorText)
      }
    } catch (error) {
      alert('Error adding activity: ' + error.message)
    }
  }

  // Update activity numbers in a container
  updateActivityNumbers(container) {
    const activities = container.querySelectorAll('[data-activity-card]')
    activities.forEach((activity, index) => {
      const numberBadge = activity.querySelector('[data-activity-number]')
      if (numberBadge) {
        numberBadge.textContent = index + 1
      }
    })
  }

  // Update the activity count badge in phase header
  updateActivityCountBadge(dropzone) {
    // Find the phase container
    const phaseContainer = dropzone.closest('[data-phase-id]')
    if (!phaseContainer) return

    const phaseId = phaseContainer.dataset.phaseId

    // Find the activity count badge in the phase header
    const countBadge = phaseContainer.querySelector('[data-activity-count]')
    if (!countBadge) return

    // Count activities in this phase
    const activitiesContainer = dropzone.querySelector('[data-activities-container]')
    const activityCount = activitiesContainer ? activitiesContainer.querySelectorAll('[data-activity-card]').length : 0

    // Update badge text with proper pluralization
    const countText = countBadge.querySelector('[data-activity-count-text]')
    if (countText) {
      const activityWord = activityCount === 1 ? 'activity' : 'activities'
      countText.textContent = `${activityCount} ${activityWord}`
    }

    // Update data attribute
    countBadge.setAttribute('data-activity-count-value', activityCount)

    // Show or hide badge based on count
    if (activityCount > 0) {
      countBadge.style.display = ''
    } else {
      countBadge.style.display = 'none'
    }

    // Also update the phases sidebar count
    this.updatePhasesSidebarCount(phaseId, activityCount)
  }

  // Update the activity count in the phases sidebar
  updatePhasesSidebarCount(phaseId, activityCount) {
    // Find the sidebar card for this phase
    const sidebarCard = document.querySelector(`[data-phase-sidebar-id="${phaseId}"]`)
    if (!sidebarCard) return

    // Find the count text element
    const countText = sidebarCard.querySelector('[data-phase-sidebar-count-text]')
    if (!countText) return

    // Update with proper pluralization
    const activityWord = activityCount === 1 ? 'activity' : 'activities'
    countText.textContent = `${activityCount} ${activityWord}`
  }

  // Handle delete activity card
  async deleteActivity(event) {
    event.preventDefault()
    event.stopPropagation() // Stop event from bubbling to draggable handlers

    const activityCard = event.currentTarget.closest('[data-activity-card]')
    if (!activityCard) {
      return
    }

    // Get activity ID if it exists (saved activities have IDs)
    const rawActivityId = activityCard.dataset.activityId
    // Remove any quotes that might be in the ID
    const activityId = rawActivityId ? rawActivityId.replace(/['"]/g, '') : null

    // Find the dropzone that contains this activity card
    const dropzone = activityCard.closest('[data-activity-drag-drop-target="dropzone"]')

    // If activity has an ID, delete from backend first
    if (activityId) {
      try {
        // Get inloop ID from URL or data attribute
        const urlParts = window.location.pathname.split('/')
        const inloopId = urlParts[urlParts.indexOf('inloops') + 1]

        const response = await fetch(`/facilitator/inloops/${inloopId}/activities/${activityId}`, {
          method: 'DELETE',
          headers: {
            'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
            'Accept': 'text/vnd.turbo-stream.html, application/json'
          }
        })

        if (!response.ok) {
          alert('Failed to delete activity from database')
          return
        }

        // If response is turbo stream, let Turbo handle it
        const contentType = response.headers.get('content-type')
        if (contentType && contentType.includes('turbo-stream')) {
          const html = await response.text()
          Turbo.renderStreamMessage(html)
          return // Turbo stream will handle UI updates
        }
      } catch (error) {
        console.error('Error deleting activity:', error)
        alert('Error deleting activity: ' + error.message)
        return
      }
    }

    // Remove the activity card with animation (fallback for non-saved activities)
    activityCard.style.transition = 'opacity 0.3s ease-out'
    activityCard.style.opacity = '0'

    setTimeout(() => {
      activityCard.remove()

      // Check if there are any remaining activities in this specific dropzone
      if (dropzone) {
        const activitiesContainer = dropzone.querySelector('[data-activities-container]')
        if (activitiesContainer && activitiesContainer.children.length === 0) {
          // Show empty state if no activities left in this dropzone
          const emptyState = dropzone.querySelector('[data-activity-empty-state]')
          if (emptyState) {
            emptyState.classList.remove('hidden')
          }
          activitiesContainer.classList.add('hidden')
        } else {
          // Update activity numbers after deletion
          this.updateActivityNumbers(activitiesContainer)
        }

        // Update activity count badge
        this.updateActivityCountBadge(dropzone)
      }

      // Dispatch custom event to notify activity selectors to refresh
      window.dispatchEvent(new CustomEvent('activity-list-changed', {
        detail: { type: 'delete' }
      }))
    }, 300)
  }
}
